import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { apiFetch } from '@/lib/api';
import { ArrowLeft, Search, ShieldOff, AlertTriangle, Mail, X, Loader2 } from 'lucide-react';

export default function AdminSuppressionsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { ready } = useRequireAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [releasingEmail, setReleasingEmail] = useState(null);
  const [toast, setToast] = useState(null);

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const showToast = (msg, tone = 'success') => {
    setToast({ msg, tone });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchSuppressions = useCallback(async (q, cursor) => {
    const qs = new URLSearchParams();
    qs.set('limit', '50');
    if (q) qs.set('q', q);
    if (cursor) qs.set('cursor', cursor);
    return apiFetch(`/api/v1/admin/email-suppressions?${qs.toString()}`);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchSuppressions(query, null);
      setItems(r.items || []);
      setNextCursor(r.next_cursor || null);
    } catch (e) {
      if (e.status === 403) {
        setError('Access denied. SuperAdmin role required.');
      } else {
        setError(e?.message || 'Could not load suppressions.');
      }
    } finally {
      setLoading(false);
    }
  }, [query, fetchSuppressions]);

  useEffect(() => {
    if (ready && isSuperAdmin) load();
  }, [ready, isSuperAdmin, load]);

  const onLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await fetchSuppressions(query, nextCursor);
      setItems((prev) => [...prev, ...(r.items || [])]);
      setNextCursor(r.next_cursor || null);
    } catch (e) {
      showToast(e?.message || 'Failed to load more.', 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    setQuery(searchInput.trim());
  };

  const onRelease = async (email) => {
    setReleasingEmail(email);
    try {
      await apiFetch(`/api/v1/admin/email-suppressions/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      });
      setItems((prev) => prev.filter((r) => r.email !== email));
      showToast(`Released ${email} — future sends are allowed.`);
    } catch (e) {
      const msg = e?.code === 'NOT_SUPPRESSED' ? 'Already released.' : (e?.message || 'Release failed.');
      showToast(msg, 'error');
    } finally {
      setReleasingEmail(null);
    }
  };

  if (!ready) return null;

  if (!isSuperAdmin) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center" data-testid="admin-suppressions-forbidden">
        <ShieldOff className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <h1 className="text-xl font-extrabold text-[#0B1B46] mb-2">Access Denied</h1>
        <p className="text-sm text-[#5B6472]">This page is restricted to SuperAdmin accounts.</p>
        <button onClick={() => navigate('/settings')} className="mt-4 h-10 px-5 bg-[#146BFF] text-white rounded-lg font-semibold text-sm hover:bg-[#0F55CC] transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF]" data-testid="admin-suppressions-back-btn">Back to Settings</button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-24" data-testid="admin-suppressions-page">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-16 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-lg shadow-lg text-sm font-semibold ${
          toast.tone === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'
        }`} data-testid="admin-suppressions-toast">{toast.msg}</div>
      )}

      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline focus:outline-none focus:ring-2 focus:ring-[#146BFF] rounded" data-testid="admin-suppressions-back">
        <ArrowLeft className="w-4 h-4" /> back
      </button>

      <p className="text-[11px] font-extrabold tracking-[1.5px] text-red-500 mb-1">SUPERADMIN</p>
      <h1 className="text-2xl font-extrabold text-[#0B1B46]" data-testid="admin-suppressions-title">Email Suppressions</h1>
      <p className="text-sm text-[#5B6472] mt-1 mb-6">Bounced and complained emails. Releasing allows future sends.</p>

      {/* Search */}
      <form onSubmit={onSearch} className="flex gap-2 mb-5">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6472]" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by email…"
            className="w-full h-10 pl-9 pr-3 border border-[#E3E8EF] rounded-lg text-sm text-[#0B1B46] placeholder-[#A0A8B4] focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:border-transparent"
            data-testid="admin-suppressions-search-input"
          />
        </div>
        <button type="submit" className="h-10 px-5 bg-[#146BFF] text-white rounded-lg font-semibold text-sm hover:bg-[#0F55CC] transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF]" data-testid="admin-suppressions-search-btn">Search</button>
        {query && (
          <button type="button" onClick={() => { setSearchInput(''); setQuery(''); }} className="h-10 px-3 border border-[#E3E8EF] text-[#5B6472] rounded-lg hover:bg-[#F3F5F8] transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF]" data-testid="admin-suppressions-clear-btn">
            <X className="w-4 h-4" />
          </button>
        )}
      </form>

      {loading ? (
        <div className="flex justify-center py-16" data-testid="admin-suppressions-loading">
          <div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5" data-testid="admin-suppressions-error">
          <p className="text-sm text-red-700 font-semibold">{error}</p>
          <button onClick={load} className="mt-3 h-10 px-5 bg-[#146BFF] text-white rounded-lg font-semibold text-sm hover:bg-[#0F55CC] transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF]" data-testid="admin-suppressions-retry-btn">Retry</button>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-[#F4F8FF] border border-[#D0DFFF] rounded-xl p-6 text-center" data-testid="admin-suppressions-empty">
          <Mail className="w-10 h-10 text-[#146BFF] mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#0B1B46]">{query ? 'No suppressions match your search.' : 'No active email suppressions.'}</p>
          <p className="text-xs text-[#5B6472] mt-1">Bounced and complained emails will appear here automatically.</p>
        </div>
      ) : (
        <>
          <div className="bg-white border border-[#E3E8EF] rounded-xl divide-y divide-[#E3E8EF]" data-testid="admin-suppressions-list">
            {items.map((row) => (
              <div key={row.email} className="px-4 py-3.5 flex items-center gap-3" data-testid={`admin-suppression-row-${row.email}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                  row.reason === 'complaint' ? 'bg-red-100' : 'bg-amber-100'
                }`}>
                  <AlertTriangle className={`w-4 h-4 ${row.reason === 'complaint' ? 'text-red-500' : 'text-amber-500'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#0B1B46] truncate" data-testid={`admin-suppression-email-${row.email}`}>{row.email}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                      row.reason === 'complaint' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                    }`} data-testid={`admin-suppression-reason-${row.email}`}>{row.reason}</span>
                    {row.extra?.bounce_type && (
                      <span className="text-[10px] text-[#5B6472]">{row.extra.bounce_type}{row.extra?.bounce_subtype ? ` / ${row.extra.bounce_subtype}` : ''}</span>
                    )}
                    <span className="text-[10px] text-[#5B6472]">{row.created_at ? new Date(row.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                  </div>
                </div>
                <button
                  onClick={() => onRelease(row.email)}
                  disabled={releasingEmail === row.email}
                  className="h-8 px-3 text-xs font-semibold border border-[#E3E8EF] text-[#0B1B46] rounded-lg hover:bg-[#F3F5F8] transition-colors disabled:opacity-50 flex items-center gap-1.5 shrink-0 focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
                  data-testid={`admin-suppression-release-${row.email}`}
                >
                  {releasingEmail === row.email ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <ShieldOff className="w-3.5 h-3.5" />
                  )}
                  Release
                </button>
              </div>
            ))}
          </div>

          {nextCursor && (
            <div className="flex justify-center mt-4">
              <button
                onClick={onLoadMore}
                disabled={loadingMore}
                className="h-10 px-6 border border-[#E3E8EF] text-[#0B1B46] rounded-lg font-semibold text-sm hover:bg-[#F3F5F8] transition-colors disabled:opacity-50 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
                data-testid="admin-suppressions-load-more-btn"
              >
                {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                Load more
              </button>
            </div>
          )}

          <p className="text-xs text-[#5B6472] text-center mt-4">
            Showing {items.length} active suppression{items.length !== 1 ? 's' : ''}.
            {query && ' Filtered by: ' + query}
          </p>
        </>
      )}
    </div>
  );
}
